import { useEffect, useState, useCallback, useRef } from "react";
import winnerIcon from "../../assets/images/winner-icon.png";
import {
  createOrUpdateSeasonChallenge,
  getChallengeDetails,
} from "./firestoreQuery";
import { uploadImage } from "../../services/firebase";
import { useParams } from "react-router";
import { IconMinus, IconPlus } from "../CustomIcons";
import SelectPunishment from "./SeasonChallengeSelectPunishmentPage";
import { SectionLoader } from "../ui/SectionLoader";

interface IAddEditChallengeProps {
  leagueData: any;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  createdChallengeId?: string | null;
}

interface Distribution {
  name: string;
  percentage: number;
}

const getPlaceName = (index: number) => {
  if (index === 0) return "1st Place";
  if (index === 1) return "2nd Place";
  if (index === 2) return "3rd Place";
  return `${index + 1}th Place`;
};

// helper to force Last Place to last
const enforcePunishmentFundLast = (dist: Distribution[]) => {
  const winners = dist.filter((d) => d.name !== "Last Place");
  const punishment = dist.find((d) => d.name === "Last Place");
  return punishment ? [...winners, punishment] : winners;
};

// helper to rename only winners
const renameWinners = (dist: Distribution[]) => {
  const winners = dist.filter((d) => d.name !== "Last Place");
  const renamed = winners.map((d, i) => ({ ...d, name: getPlaceName(i) }));
  const punishment = dist.find((d) => d.name === "Last Place");
  return punishment ? [...renamed, punishment] : renamed;
};

const AddEditChallengeStakes: React.FC<IAddEditChallengeProps> = ({
  leagueData,
  setSelectedTab,
  createdChallengeId,
}) => {
  const { challengeId } = useParams();
  // const [challengeData, setChallengeData] = useState<any>(null); // not used in this view
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Ref for entry fee input to auto-focus when screen loads
  const entryFeeInputRef = useRef<HTMLInputElement>(null);

  const [totalPot, setTotalPot] = useState(0);
  const [entryFee, setEntryFee] = useState<number | null>(null);
  // Start with 1st place only, no prefilled percentage (user will enter amount to derive percentage)
  const [distribution, setDistribution] = useState<Distribution[]>([
    { name: "1st Place", percentage: 0 },
  ]);
  // Raw amount entries per place (undefined = not entered yet)
  const [amounts, setAmounts] = useState<(number | undefined)[]>([undefined]);
  const [isLastPosition, setIsLastPosition] = useState(true); // controls whether last place punishment fund participates
  const [message, setMessage] = useState({ data: "", class: "" });
  const [amountsNotEqualTotal, setAmountsNotEqualTotal] = useState(false);

  const [punishmentData, setPunishmentData] = useState<any>(null);
  const [challengeAllTeams, setChallengeAllTeams] = useState<any>(null);
  const [challengeStatus, setChallengeStatus] = useState<string | null>(null);
  // Track whether we're editing an existing challenge (to trigger auto amount recalculation on totalPot changes)
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  // Track if any place has zero/undefined amount
  const [hasZeroAmount, setHasZeroAmount] = useState<boolean>(false);
  // Fetch challenge details (excluding isLastPosition from deps to prevent refetch/reset on toggle)
  const getAllChallengeDetails = useCallback(async () => {
    try {
      setIsLoading(true);

      const res = await getChallengeDetails(
        leagueData?.id,
        createdChallengeId ?? ""
      );
      // Hydrate state for edit mode if we got a response
      if (res) {
        setChallengeAllTeams(res.teams || []);
        setChallengeStatus(res.challengeCreationStatus || "draft");
        const stakes =
          (res as any).challengeStakes || (res as any).challenge_stakes || null;
        if (stakes) {
          // Mark edit mode (we have existing stakes data)
          setIsEditMode(true);
          // Entry Fee
          const fetchedEntryFee =
            typeof stakes.entryFee === "number" ? stakes.entryFee : null;
          setEntryFee(fetchedEntryFee);

          // Punishment fund flag
          // Punishment fund participation controlled solely by isLastPosition now

          // Distribution transformation
          const rawDist: any[] = Array.isArray(stakes.distribution)
            ? stakes.distribution
            : [];
          let transformed: Distribution[] = rawDist.map((d) => ({
            name: d.place || d.name || d.placeName || d.position || "",
            percentage:
              typeof d.percentage === "number"
                ? d.percentage
                : Number(d.percentage) || 0,
          }));

          // Filter out empties
          transformed = transformed.filter((d) => d.name);

          const hasLastPlace = transformed.some(
            (d) => d.name === "Last Place" && d.percentage > 0
          );
          // Separate winners & last place; rename winners to ordinal labels
          const winners = transformed.filter((d) => d.name !== "Last Place");
          const renamedWinners = winners.map((d, i) => ({
            ...d,
            name: getPlaceName(i),
          }));
          const lastPlacePart = hasLastPlace
            ? transformed.filter((d) => d.name === "Last Place")
            : [];
          const finalDist = enforcePunishmentFundLast([
            ...renamedWinners,
            ...lastPlacePart,
          ]);
          // Compute potential total pot early for amount prefill (derivedPot not yet declared below)
          const preDerivedPot =
            fetchedEntryFee && res.teams
              ? fetchedEntryFee * +res.teams.length
              : 0;
          const currentTotalPotForPrefill =
            typeof stakes.totalPot === "number"
              ? stakes.totalPot
              : preDerivedPot;
          if (finalDist.length) {
            setDistribution(finalDist);
            setIsLastPosition(Boolean(res?.punishment));
            // Prefill amounts for edit mode using raw distribution data if present.
            // Only set a numeric amount if percentage > 0; otherwise undefined.
            const amountsPrefill = finalDist.map((fd) => {
              if (fd.percentage === 0) return undefined;
              if (fd.name === "Last Place") {
                const lpRaw = rawDist.find(
                  (r) =>
                    (r.place || r.name || r.placeName || r.position || "") ===
                    "Last Place"
                );
                const rawAmount =
                  lpRaw && typeof lpRaw.amount === "number"
                    ? lpRaw.amount
                    : null;
                return rawAmount !== null
                  ? rawAmount
                  : currentTotalPotForPrefill > 0
                  ? Math.round(
                      (fd.percentage / 100) * currentTotalPotForPrefill
                    )
                  : undefined;
              } else {
                const winnerIdx = renamedWinners.findIndex(
                  (rw) => rw.name === fd.name
                );
                const originalWinnerRaw = winners[winnerIdx];
                const rawAmount =
                  originalWinnerRaw && (originalWinnerRaw as any).amount;
                return typeof rawAmount === "number"
                  ? rawAmount
                  : currentTotalPotForPrefill > 0
                  ? Math.round(
                      (fd.percentage / 100) * currentTotalPotForPrefill
                    )
                  : undefined;
              }
            });
            setAmounts(amountsPrefill);
          }

          const derivedPot =
            fetchedEntryFee && res.teams
              ? fetchedEntryFee * +res.teams.length
              : 0;
          setTotalPot(
            typeof stakes.totalPot === "number" ? stakes.totalPot : derivedPot
          );
        }

        // Punishment object if present
        const punishment =
          (res as any).punishment || (res as any).challengePunishment || null;
        if (punishment) {
          // Pass along in a normalized shape expected by child (avoid leftover local file refs)
          setPunishmentData({
            ...punishment,
            previewImage: null,
            localFile: null,
          });
        }

        // Ensure Last Place row exists even if none fetched so user can toggle it
        // Ensure Last Place row exists even if none fetched so user can toggle it (no default percentage)
        setDistribution((prev) => {
          if (prev.some((d) => d.name === "Last Place")) return prev;
          return enforcePunishmentFundLast([
            ...prev,
            { name: "Last Place", percentage: 0 },
          ]);
        });
      }
    } catch (error) {
      console.error("Error fetching challenge details:", error);
    } finally {
      setIsLoading(false);
    }
  }, [createdChallengeId, leagueData?.id]);

  useEffect(() => {
    getAllChallengeDetails();
  }, [getAllChallengeDetails]);

  // 🔹 Calculate TotalPot
  useEffect(() => {
    if (entryFee && entryFee > 0 && challengeAllTeams) {
      setTotalPot(entryFee * +challengeAllTeams.length);
    } else {
      setTotalPot(0);
    }
  }, [entryFee, challengeAllTeams]);

  // When totalPot changes in edit mode, recalculate the raw amount allocations from percentages
  // Requirement: "if the totalpot is changed then recalculate the distribution and fill them as per their percentage in edit mode."
  useEffect(() => {
    if (!isEditMode) return; // Only auto-recalculate for existing challenges
    if (!totalPot || totalPot <= 0) {
      // totalPot is 0: clear all stakes (amount inputs)
      setAmounts(distribution.map(() => undefined));
      return;
    }
    // Recompute each amount strictly from its percentage of the (new) totalPot
    setAmounts(() =>
      distribution.map((d) => {
        if (d.name === "Last Place" && !isLastPosition) return undefined;
        if (d.percentage === 0) return undefined;
        const computed = (d.percentage / 100) * totalPot;
        return Math.round(computed);
      })
    );
  }, [totalPot, isEditMode, distribution, isLastPosition]);

  // In creation mode (not edit), if entry fee / totalPot changes AFTER user entered raw amounts,
  // recompute the percentages so they reflect the same absolute amounts against the new totalPot.
  // This keeps the UI math accurate and triggers proper validation.
  useEffect(() => {
    if (isEditMode) return; // edit mode already handled above
    if (!totalPot || totalPot <= 0) {
      // Reset percentages when pot is zero
      setDistribution((prev) => prev.map((d) => ({ ...d, percentage: 0 })));
      return;
    }
    setDistribution((prev) =>
      prev.map((d, i) => {
        if (d.name === "Last Place" && !isLastPosition) {
          return { ...d, percentage: 0 };
        }
        const amt = amounts[i];
        if (typeof amt === "number" && amt >= 0) {
          return { ...d, percentage: (amt / totalPot) * 100 };
        }
        return d; // untouched if no amount yet
      })
    );
  }, [totalPot, isEditMode, amounts, isLastPosition]);

  // Only allow integer input > 0 for entry fee
  const handleOnEntryFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (amountsNotEqualTotal) {
      setMessage({
        data: "Check your math, stakes must equal 100%",
        class: "errorTxt",
      });
    }
    // Remove all non-digit characters
    let val = e.target.value.replace(/[^\d]/g, "");
    // Restrict to max 12 digits (max value 999999999999)
    if (val.length > 12) val = val.slice(0, 12);
    const maxVal = 999999999999;
    let num = val === "" ? null : Number(val);
    if (val !== "" && num !== null && num > maxVal) {
      num = maxVal;
      val = String(maxVal);
    }
    if (val === "") {
      setEntryFee(null);
    } else if (
      num !== null &&
      !isNaN(num) &&
      Number.isInteger(num) &&
      num > 0 &&
      num <= maxVal
    ) {
      setEntryFee(num);
    } else {
      setEntryFee(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leagueData?.id) return;
    let finalPunishment = punishmentData;
    // If custom punishment has a local file to upload, do it now
    if (
      finalPunishment?.isCustomPunishment &&
      finalPunishment?.localFile instanceof File
    ) {
      try {
        const uploadedUrl = await uploadImage(finalPunishment.localFile);
        finalPunishment = {
          ...finalPunishment,
          cardImage: uploadedUrl,
          // Remove localFile after upload to keep stored object clean
          localFile: null,
          previewImage: null,
        };
      } catch (err) {
        console.error("Punishment image upload failed:", err);
      }
    }
    // If last place punishment is disabled, ensure punishment is null
    if (!isLastPosition) {
      finalPunishment = null;
    }
    const derivedDistribution = distribution.map((d, i) => {
      const amount =
        typeof amounts[i] === "number"
          ? amounts[i]!
          : (totalPot * d.percentage) / 100;
      const pct = totalPot > 0 ? (amount / totalPot) * 100 : 0;
      return { place: d.name, percentage: pct, amount };
    });
    const saveData = {
      leagueId: leagueData.id,
      challengeCreationStatus: challengeStatus || "draft",
      challengeStakes: {
        entryFee,
        // isPunishmentFund deprecated; inclusion handled by isLastPosition toggle
        isPunishmentFund: isLastPosition, // keep field for backward compatibility (true when last place enabled)
        totalPot,
        distribution: derivedDistribution,
      },
      punishment: finalPunishment || null,
    };
    try {
      if (challengeId) {
        await createOrUpdateSeasonChallenge(saveData, challengeId);
      } else {
        await createOrUpdateSeasonChallenge(
          saveData,
          createdChallengeId ?? undefined
        );
      }
    } catch (error) {
      console.log(error);
    }
    setSelectedTab(3);
  };

  const handleRemove = (index: number) => {
    const winners = distribution.filter((d) => d.name !== "Last Place");
    if (index === 0 || index >= winners.length) return; // can't remove 1st place or punishment
    const newWinners = winners.filter((_, i) => i !== index);
    const newDist = renameWinners([
      ...newWinners,
      ...distribution.filter((d) => d.name === "Last Place"),
    ]);
    setDistribution(newDist);
    // Rebuild amounts (drop removed winner, keep last place entry if present)
    setAmounts((prev) => {
      const lastIdxPrev = distribution.findIndex(
        (d) => d.name === "Last Place"
      );
      const lastAmountPrev = lastIdxPrev !== -1 ? prev[lastIdxPrev] : undefined;
      const prevWinnerAmounts = prev.filter(
        (_, i) => i !== index && i !== lastIdxPrev
      );
      const next: (number | undefined)[] = [];
      const winnerCount = newDist.filter((d) => d.name !== "Last Place").length;
      for (let i = 0; i < winnerCount; i++) next[i] = prevWinnerAmounts[i];
      if (newDist.some((d) => d.name === "Last Place")) {
        next[newDist.length - 1] = lastAmountPrev;
      }
      return next;
    });
  };

  const handleAdd = () => {
    const winners = distribution.filter((d) => d.name !== "Last Place");
    if (winners.length >= 5) return; // max 5 winners
    const lastIdx = distribution.findIndex((d) => d.name === "Last Place");
    const lastPlaceEntry = lastIdx !== -1 ? distribution[lastIdx] : null;

    // Add new winner with 0% (user will set amount manually)
    const newWinners = [
      ...winners,
      { name: getPlaceName(winners.length), percentage: 10 },
    ];
    const newDist = renameWinners([
      ...newWinners,
      ...(lastPlaceEntry ? [lastPlaceEntry] : []),
    ]);
    setDistribution(newDist);
    setAmounts((prev) => {
      if (lastIdx === -1) return [...prev, undefined]; // no last place present
      const lastPlaceAmount = prev[lastIdx];
      const winnerAmounts = prev.slice(0, lastIdx); // amounts for existing winners
      // Insert undefined for new winner before last place amount
      const next = [...winnerAmounts, undefined, lastPlaceAmount];
      return next;
    });
  };

  // Accept raw amount (>=0) and convert to percentage of totalPot
  const handleAmountChange = (index: number, rawValue: string) => {
    let cleaned = rawValue.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      cleaned = parts[0] + "." + parts[1];
    }
    if (cleaned === "") {
      setAmounts((prev) => {
        const next = [...prev];
        next[index] = undefined;
        return next;
      });
      // Also reset percentage to 0 when clearing the input
      setDistribution((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], percentage: 0 };
        return updated;
      });
      return;
    }
    const amount = Number(cleaned);
    if (isNaN(amount) || amount < 0) return;
    if (totalPot > 0 && amount > totalPot) return; // enforce 0..totalPot
    // If user enters 0, treat as clearing => percentage 0 and amount undefined
    if (amount === 0) {
      setAmounts((prev) => {
        const next = [...prev];
        next[index] = undefined;
        return next;
      });
      setDistribution((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], percentage: 0 };
        return updated;
      });
      return;
    }
    setAmounts((prev) => {
      const next = [...prev];
      next[index] = amount;
      return next;
    });
    if (totalPot > 0) {
      setDistribution((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          percentage: (amount / totalPot) * 100,
        };
        return updated;
      });
    }
  };

  // Check if any winner place (not Last Place) has zero/undefined amount
  useEffect(() => {
    if (!entryFee || entryFee <= 0) {
      setHasZeroAmount(false);
      return;
    }
    
    // Check if any winner place (1st, 2nd, 3rd, etc.) has 0 or undefined amount
    // Last Place is allowed to be undefined or any amount > 0
    const hasZero = distribution.some((d, i) => {
      if (d.name === "Last Place") return false; // Last Place can be undefined or > 0
      if (!isLastPosition && d.name === "Last Place") return false; // exclude when disabled anyway
      const amount = amounts[i];
      return typeof amount !== "number" || amount === 0;
    });
    
    setHasZeroAmount(hasZero);
  }, [amounts, distribution, entryFee, isLastPosition]);

  // totalAmounts no longer needed for validation; percentages drive messaging
  // New effect: validate that sum of entered amounts does not exceed totalPot
  useEffect(() => {
    if (!totalPot) {
      setAmountsNotEqualTotal(false);
      return;
    }
    // Sum only visible amounts (exclude Last Place when disabled or when undefined)
    const sum = distribution.reduce((acc, d, i) => {
      // Skip Last Place if disabled
      if (d.name === "Last Place" && !isLastPosition) return acc;
      // Skip Last Place if undefined (optional field)
      if (d.name === "Last Place" && typeof amounts[i] !== "number") return acc;
      
      const amt =
        typeof amounts[i] === "number"
          ? amounts[i]!
          : (totalPot * d.percentage) / 100;
      return acc + amt;
    }, 0);
    // Allow small floating point tolerance
    console.log("---------", sum - totalPot);
    if (Math.floor(sum - totalPot) !== 0) {
      setAmountsNotEqualTotal(true);
    } else {
      setAmountsNotEqualTotal(false);
    }
  }, [amounts, distribution, totalPot, isLastPosition,entryFee]);
  // Focus the entry fee input once loading completes
  useEffect(() => {
    if (!isLoading) {
      entryFeeInputRef.current?.focus();
      entryFeeInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isLoading]);
  // Ensure Last Place entry always present; managed directly via toggle handler

  const totalPercentage = distribution.reduce((sum, d) => {
    if (d.name === "Last Place" && !isLastPosition) return sum; // exclude when disabled
    return sum + d.percentage;
  }, 0);

  useEffect(() => {
    // If amounts already not equal to the total, don't overwrite that error
    const percentageOff = Math.abs(totalPercentage - 100) > 0.01;
    if (percentageOff || amountsNotEqualTotal || hasZeroAmount) {
      setMessage({
        data: "Check your math, stakes must equal 100%",
        class: "errorTxt",
      });
    } else {
      setMessage({ data: "🎉 100% of the funds are being used", class: "" });
    }
  }, [
    totalPercentage,
    distribution,
    isLastPosition,
    totalPot,
    amountsNotEqualTotal,
    entryFee,
    hasZeroAmount,
  ]);

  // Form valid only if: Entry fee > 0, distribution sums to 100, and
  // if last place punishment is enabled then a valid punishment is selected/built
  const percentageIsComplete = Math.abs(totalPercentage - 100) < 0.01;
  const isFormValid =
    !!entryFee &&
    entryFee > 0 &&
    percentageIsComplete &&
    (!isLastPosition || (punishmentData && punishmentData.isValid)) &&
    !amountsNotEqualTotal &&
    !hasZeroAmount;

  console.log("totalPot:", totalPot, amountsNotEqualTotal);

  if (isLoading) {
    return <SectionLoader />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="yourLeagueHeader">
        <div className="headingItem">Define the Stakes</div>
      </div>
      <div className="shadowBlk">
        <div className="formGroup">
          <label>
            Entry Fee <span className="reqTxt">Required</span>
          </label>
          <div className="fldHavePrefix">
            <span className="fldPrefix">$</span>
            <input
              className="fldStyle"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              ref={entryFeeInputRef}
              value={entryFee ?? undefined}
              maxLength={12}
              onChange={handleOnEntryFeeChange}
            />
          </div>
        </div>
        <div className="priceContWrap">
          <div className="priceBlk">
            <strong>${Number(totalPot ?? 0).toLocaleString()}</strong>
            <span>Total Pot</span>
          </div>
        </div>
      </div>
      <div className="shadowBlk">
        <div className="potContentWrap">
          {distribution.map((d, index) => {
            // Check if this is the Last Place row
            const isLastPlaceRow = d.name === "Last Place";
            
            return (
              <div key={index}>
                {/* Add button before Last Place */}
                {isLastPlaceRow && distribution.filter((d) => d.name !== "Last Place").length < 5 && (
                  <div className="potItemRow formGroup">
                    <div className="iconCol">
                      <button onClick={handleAdd} type="button">
                        <IconPlus />
                      </button>
                    </div>
                    <div className="positionCol cursorPointer">
                      <span
                        className="positionTxt"
                        onClick={handleAdd}
                        style={{ cursor: "pointer" }}
                      >
                        Add
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Distribution row */}
                <div className="potItemRow formGroup">
                  <div className="iconCol">
                    {isLastPlaceRow ? (
                      <label className="switchOption" title="Toggle Last Place">
                        <input
                          type="checkbox"
                          checked={isLastPosition}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setIsLastPosition(checked);
                            // Update distribution percentage for Last Place with immediate validation
                            setDistribution((prev) => {
                              const updated = prev.map((d2) => {
                                if (d2.name !== "Last Place") return d2;
                                if (!checked) return { ...d2, percentage: 0 };
                                return d2;
                              });

                              return updated;
                            });
                            // Reset amount when disabling
                            if (!checked) {
                              const lastIdx = distribution.findIndex(
                                (x) => x.name === "Last Place"
                              );
                              setAmounts((prev) => {
                                const next = [...prev];
                                if (lastIdx !== -1) next[lastIdx] = undefined;
                                return next;
                              });
                              setPunishmentData(null);
                            }
                          }}
                        />
                        <span
                          className={`customCheck ${
                            isLastPosition ? "haveChecked" : ""
                          }`}
                        >
                          <span
                            className="checkCircle"
                            style={{
                              transform: isLastPosition
                                ? "translateX(26px)"
                                : "translateX(0)",
                            }}
                          />
                        </span>
                      </label>
                    ) : index === 0 ? (
                      <img src={winnerIcon} alt="" />
                    ) : (
                      <button onClick={() => handleRemove(index)} type="button">
                        <IconMinus />
                      </button>
                    )}
                  </div>
                  <div className="positionCol">
                    <span className="positionTxt">{d.name}</span>
                    <span className="potPercentage">
                      {totalPot > 0 && typeof amounts[index] === "number"
                        ? `${d.percentage.toFixed(2).replace(/\.00$/, "")}%`
                        : ""}
                    </span>
                  </div>
                  <div className="fldHavePrefix">
                    <span className="fldPrefix">$</span>
                    <input
                      className="fldStyle"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      disabled={
                        totalPot <= 0 ||
                        (isLastPlaceRow && !isLastPosition)
                      }
                      placeholder={
                        isLastPlaceRow ? "Last Place Fund?" : ""
                      }
                      value={
                        typeof amounts[index] === "number"
                          ? amounts[index]!.toString()
                          : ""
                      }
                      onChange={(e) => handleAmountChange(index, e.target.value)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <p className={`noteTxt ${message.class}`}>{message.data}</p>
        </div>
      </div>

      {isLastPosition && (
        <div className="shadowBlk selectPunishmentBlk">
          <SelectPunishment
            leagueData={leagueData}
            setSelectedTab={setSelectedTab}
            setPunishmentData={setPunishmentData}
            punishmentData={punishmentData}
          />
        </div>
      )}

      <div className="btnWrap">
        <button
          type="button"
          className="btnStyle "
          onClick={() => setSelectedTab(1)}
        >
          CANCEL
        </button>
        <button
          type="submit"
          className="btnStyle btnFilled"
          disabled={!isFormValid}
        >
          CONTINUE
        </button>
      </div>
    </form>
  );
};

export default AddEditChallengeStakes;
